import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import { useVirtualizer } from "@tanstack/react-virtual";
import ColorCard from "components/ColorCard";
import useProjectArticles from "./useProjectArticles";
import useNavigation from "helpers/useNavigation";
import { getShareRedirect } from "services/shareBridge";
import { getV2ShareUrl } from "services/inventoryPublic";

/**
 * Terminal page for a share whose project moved to the new platform and was
 * then deleted or unpublished there. Deliberately NOT the stale frozen V1
 * copy: after a project moves, the customer-facing kill switch lives on the
 * new platform, so V1 must not keep serving what the rep withdrew.
 */
const ShareGonePage = () => (
  <div className="flex h-full w-full flex-col items-center justify-center gap-8 bg-secondary">
    <div className="flex flex-col items-center pb-2">
      <img
        src="/stormtech.svg"
        alt="logo"
        className="mb-2 w-[75vw] md:w-[300px]"
      />
    </div>
    <div className="flex flex-col items-center gap-1 px-4 text-center">
      <p className="text-xl font-bold text-st-grey">
        This shared project is no longer available
      </p>
      <p className="text-st-grey">
        Please contact the person who shared it for an updated link.
      </p>
    </div>
  </div>
);

export const ProjectViewerPage = () => {
  const { projID } = useParams();
  const [navigate] = useNavigation();

  const [project, setProject] = useState();
  // null = bridge check in flight; then { state: "v1" | "gone" }. "redirect"
  // never lands in state — the page is replaced with the V2 share URL.
  const [bridge, setBridge] = useState(null);

  const selectedItems = useMemo(() => {
    return project?.selectedItems?.map((item) => item.id);
  }, [project?.selectedItems]);

  const { articlesData, currentInventory, currentInventoryLoading } =
    useProjectArticles(
      selectedItems,
      project?.warehouses,
    );

  // Virtualize the card list so only the on-screen rows mount. A large project
  // (300+ items) otherwise renders hundreds of heavy ColorCards at once, which
  // freezes the viewer for the customer.
  const scrollRef = useRef(null);
  const flatColors = useMemo(
    () => articlesData.flatMap((item) => item.colors),
    [articlesData],
  );
  const rowVirtualizer = useVirtualizer({
    count: flatColors.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 420,
    measureElement: (el) => el.getBoundingClientRect().height,
    overscan: 6,
  });

  // Authority bridge check, BEFORE any Firestore read (spec 7, Phase 1).
  // Fail-open: any bridge problem renders the V1 viewer exactly as today.
  useEffect(() => {
    let cancelled = false;
    getShareRedirect(projID).then((result) => {
      if (cancelled) return;
      if (result.state === "redirect") {
        // The project's data authority moved to the new platform; its share
        // page moved with it. replace() keeps Back working for the customer.
        window.location.replace(getV2ShareUrl(result.shareLinkId));
        return;
      }
      setBridge(result);
    });
    return () => {
      cancelled = true;
    };
  }, [projID]);

  useEffect(() => {
    if (bridge?.state !== "v1") return;
    const db = getFirestore();
    getDoc(doc(db, "projects", projID)).then((doc) => {
      if (!doc.exists()) {
        navigate("/public/projects");
      }
      setProject({
        ...doc.data(),
        id: doc.id,
        createdAt: doc.data().createdAt.toDate(),
        modifiedAt: doc.data().modifiedAt.toDate(),
      });
    });
  }, [bridge, navigate, projID]);

  if (bridge?.state === "gone") {
    return <ShareGonePage />;
  }

  return (
    <div className="grid h-full w-full grid-rows-[auto_1fr_auto] overflow-hidden">
      <div
        // className={`flex max-h-20 w-full items-center gap-5 p-2 shadow-[0px_4px_6px_4px_${COLORS.stormtechOrange}] z-10`}
        className={`z-10 flex max-h-20 w-full items-center justify-between gap-5 p-2 shadow-md md:justify-start`}
      >
        <img
          className="h-full max-w-[150px] object-contain md:max-w-[250px]"
          src={project?.logoURL || "/stormtech_icon_placeholder.svg"}
          alt="logo"
        />
        <div className="flex flex-col">
          <div className="text-base font-bold text-text">{project?.name}</div>
          <div className="text-sm text-text">{project?.distributor}</div>
          <div className="text-sm font-extralight text-text">
            {project?.rep !== "xxxx" && project?.rep}
          </div>
        </div>
      </div>
      <div
        ref={scrollRef}
        className="z-0 h-full overflow-auto bg-secondary p-3 pb-8"
      >
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            position: "relative",
            width: "100%",
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const color = flatColors[virtualRow.index];
            return (
              <div
                key={color.id}
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${virtualRow.start}px)`,
                }}
                className="flex justify-center pb-3"
              >
                <ColorCard
                  productData={color}
                  showOversold={false}
                  showItemNumber={true}
                  selectedWarehouses={project?.warehouses}
                  currency={project?.currency}
                  liveInventory={currentInventory}
                  isLoadingInventory={currentInventoryLoading}
                />
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex h-6 w-full justify-center border-t border-st-orange py-1">
        <img
          className="h-full object-contain"
          src="/stormtech_horizontal.svg"
          alt="logo"
        />
      </div>
    </div>
  );
};
